import { useRef, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, X, ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/auth";
import { resolveImg } from "@/lib/img";

const ImageUploader = ({ images, onChange }) => {
  const inputRef = useRef(null);
  const [progress, setProgress] = useState(null);

  const upload = async (files) => {
    const list = Array.from(files).filter((f) => f.type.startsWith("image/"));
    if (!list.length) return;
    const added = [];
    for (let i = 0; i < list.length; i++) {
      const fd = new FormData();
      fd.append("file", list[i]);
      try {
        setProgress({ current: i + 1, total: list.length, pct: 0 });
        const { data } = await api.post("/admin/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (e) => e.total && setProgress({ current: i + 1, total: list.length, pct: Math.round((e.loaded / e.total) * 100) }),
        });
        added.push(data.url);
      } catch (e) {
        toast.error(`${list[i].name}: ${formatApiError(e)}`);
      }
    }
    setProgress(null);
    if (added.length) onChange([...images, ...added]);
  };

  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= images.length) return;
    const next = [...images];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };

  return (
    <div data-testid="image-uploader">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {images.map((src, i) => (
          <div key={src} className="group relative aspect-[4/3] overflow-hidden border border-white/10 bg-coal" data-testid={`uploaded-image-${i}`}>
            <img src={resolveImg(src)} alt="" className="h-full w-full object-cover" />
            {i === 0 && <span className="absolute left-2 top-2 bg-sun px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.2em] text-obsidian">Capa</span>}
            <div className="absolute inset-x-0 bottom-0 flex justify-between bg-obsidian/80 p-1.5 opacity-0 transition-opacity group-hover:opacity-100">
              <button type="button" onClick={() => move(i, -1)} aria-label="Mover para a esquerda" className="p-1 text-stone-300 hover:text-brand"><ArrowLeft size={13} /></button>
              <button type="button" onClick={() => onChange(images.filter((_, k) => k !== i))} data-testid={`remove-image-${i}`} aria-label="Remover" className="p-1 text-stone-300 hover:text-brand"><X size={13} /></button>
              <button type="button" onClick={() => move(i, 1)} aria-label="Mover para a direita" className="p-1 text-stone-300 hover:text-brand"><ArrowRight size={13} /></button>
            </div>
          </div>
        ))}
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={!!progress}
          data-testid="upload-image-button"
          className="flex aspect-[4/3] flex-col items-center justify-center gap-2 border border-dashed border-white/20 text-stone-400 transition-colors hover:border-brand hover:text-brand disabled:opacity-60"
        >
          {progress ? (
            <>
              <Loader2 size={20} className="animate-spin" />
              <span className="font-mono text-[10px] uppercase tracking-[0.2em]">{progress.current}/{progress.total} · {progress.pct}%</span>
            </>
          ) : (
            <>
              <ImagePlus size={20} />
              <span className="font-mono text-[10px] uppercase tracking-[0.2em]">Adicionar fotos</span>
            </>
          )}
        </button>
      </div>
      <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" data-testid="upload-image-input" onChange={(e) => { upload(e.target.files); e.target.value = ""; }} />
      <p className="mt-3 text-xs text-stone-500">JPG, PNG ou WEBP até 8 MB cada. A primeira foto é a capa; passe o mouse para reordenar ou remover.</p>
    </div>
  );
};

export default ImageUploader;
